import { Comment, Text, type Slot } from 'vue';

// PRODUCT-26110: Vue 2's `$slots.x` was an ARRAY of vnodes -- absent when the parent
// passed nothing AND when what it passed rendered nothing (a falsy `v-if`, an empty
// wrapper). Vue 3's `$slots.x` is a FUNCTION that exists whenever the parent mentions
// the slot at all, so `v-if="$slots.x"` / `:class="{ foo: $slots.x }"` became true in
// cases where Vue 2 said false. That silently rendered empty slot containers and
// applied "has content" modifier classes -- e.g. FormInput reserved 44px of right
// padding for a non-existent icon on the Branding and Support Contact pages.
//
// Call the slot and look for at least one vnode that actually paints something:
// comments (what a falsy `v-if` compiles to), whitespace-only text, and empty
// fragments all count as nothing.
export function hasSlotContent(slot?: Slot | null): boolean {
    if (!slot) {
        return false;
    }
    let nodes;
    try {
        nodes = slot();
    } catch {
        // A slot that throws when invoked outside its own render context is treated as
        // present rather than swallowed -- keeps behaviour conservative.
        return true;
    }
    return (nodes || []).some(function isRendered(vnode: any): boolean {
        if (!vnode || vnode.type === Comment) {
            return false;
        }
        if (vnode.type === Text) {
            return String(vnode.children ?? '').trim() !== '';
        }
        // Fragments (`<template>` wrappers, v-for) carry their real content in children.
        if (Array.isArray(vnode.children)) {
            return vnode.children.length > 0 && vnode.children.some(isRendered);
        }
        return true;
    });
}
